import React, { useRef, useState, useLayoutEffect, useEffect } from 'react';
import { gradients } from 'theme/tokens';
import { GradientBorder } from 'components/common/GradientBorder';
import type { BrainWavesData } from 'types/brainerCore';

interface BrainWavesChartProps {
  data?: BrainWavesData;
  timeframe?: string;
  bwValue?: number;
  bwDir?: string;
}

const CHART_HEIGHT = 160;
const PAD_T = 6;
const PAD_B = 6;
const PAD_L = 0;
const RIGHT_GUTTER_RATIO = 0.1;
const RIBBON_SMOOTHING = 1 / 6;
const RIBBON_SUBSEGMENTS = 10;

function lerpRGB(
  [r1, g1, b1]: [number, number, number],
  [r2, g2, b2]: [number, number, number],
  t: number,
): string {
  const c = Math.max(0, Math.min(1, t));
  return `rgb(${Math.round(r1 + (r2 - r1) * c)},${Math.round(g1 + (g2 - g1) * c)},${Math.round(
    b1 + (b2 - b1) * c,
  )})`;
}

function posColor(pos: number): string {
  if (pos < -60) return lerpRGB([21, 0, 255], [44, 106, 149], (pos + 100) / 40);
  if (pos < -20) return lerpRGB([44, 106, 149], [69, 220, 35], (pos + 60) / 40);
  if (pos < 20) return lerpRGB([69, 220, 35], [255, 247, 0], (pos + 20) / 40);
  if (pos < 60) return lerpRGB([255, 247, 0], [255, 98, 0], (pos - 20) / 40);
  return lerpRGB([255, 98, 0], [255, 0, 0], (pos - 60) / 40);
}

function parseRGB(col: string): [number, number, number] {
  const m = col.match(/\d+/g);
  return m ? [+m[0], +m[1], +m[2]] : [0, 194, 212];
}

function withAlpha(col: string, alpha: number): string {
  const [r, g, b] = parseRGB(col);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function BrainWavesChart({ data, timeframe, bwValue, bwDir }: BrainWavesChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [shown, setShown] = useState(false);

  // Measure container width responsively (replaces RN onLayout).
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setChartWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (data && chartWidth > 0 && !shown) setShown(true);
  }, [data, chartWidth, shown]);

  const h = CHART_HEIGHT;
  const chartH = h - PAD_T - PAD_B;
  const midY = PAD_T + chartH / 2;
  const padR = chartWidth * RIGHT_GUTTER_RATIO;

  const hasData = !!(
    data?.wt1?.length &&
    data?.wt2?.length &&
    data?.ribbon?.length &&
    data?.histogram?.length &&
    data.wt1.length > 1
  );

  const thresholds = data?.thresholds ?? { ob: 55, os: -55, eob: 80, eos: -80 };
  const toY = (v: number) => midY - (v / 100) * (chartH / 2);
  const toX = (i: number, n: number) => PAD_L + (i / (n - 1)) * (chartWidth - PAD_L - padR);

  const obY = toY(thresholds.ob);
  const eobY = toY(thresholds.eob);
  const osY = toY(thresholds.os);
  const eosY = toY(thresholds.eos);
  const topY = toY(100);
  const botY = toY(-100);

  const deltaText = bwValue != null ? `${bwValue > 0 ? '+' : ''}${bwValue.toFixed(1)}` : '—';
  const deltaArrow =
    bwDir?.toLowerCase().includes('up') || bwDir?.toLowerCase().includes('rising')
      ? ' ↑'
      : bwDir
        ? ' ↓'
        : '';
  const deltaColor = bwValue != null ? posColor(bwValue) : 'var(--t3)';

  // ── Build ribbon segments (Catmull-Rom → cubic bezier, per-segment color) ──
  const ribbonSegments: React.ReactNode[] = [];
  if (hasData && data) {
    const n = data.ribbon.length;
    const K = RIBBON_SUBSEGMENTS;
    const pt = (k: number): [number, number] => [toX(k, n), toY(data.ribbon[k])];
    for (let i = 1; i < n; i++) {
      const [p1x, p1y] = pt(i - 1);
      const [p2x, p2y] = pt(i);
      const [p0x, p0y] = i - 2 >= 0 ? pt(i - 2) : [p1x, p1y];
      const [p3x, p3y] = i + 1 < n ? pt(i + 1) : [p2x, p2y];
      const c1x = p1x + (p2x - p0x) * RIBBON_SMOOTHING;
      const c1y = p1y + (p2y - p0y) * RIBBON_SMOOTHING;
      const c2x = p2x - (p3x - p1x) * RIBBON_SMOOTHING;
      const c2y = p2y - (p3y - p1y) * RIBBON_SMOOTHING;
      const v0 = data.ribbon[i - 1];
      const v1 = data.ribbon[i];
      let prevX = p1x;
      let prevY = p1y;
      for (let j = 1; j <= K; j++) {
        const t = j / K;
        const omt = 1 - t;
        const b0 = omt * omt * omt;
        const b1 = 3 * omt * omt * t;
        const b2 = 3 * omt * t * t;
        const b3 = t * t * t;
        const x = b0 * p1x + b1 * c1x + b2 * c2x + b3 * p2x;
        const y = b0 * p1y + b1 * c1y + b2 * c2y + b3 * p2y;
        const tMid = (j - 0.5) / K;
        const segVal = v0 + (v1 - v0) * tMid;
        ribbonSegments.push(
          <line
            key={`r${i}-${j}`}
            x1={prevX}
            y1={prevY}
            x2={x}
            y2={y}
            stroke={posColor(segVal)}
            strokeWidth={2.5}
            strokeLinecap="round"
          />,
        );
        prevX = x;
        prevY = y;
      }
    }
  }

  return (
    <GradientBorder colors={gradients.borderDefault} borderRadius={15} borderWidth={1} innerClassName="bw-card-inner">
      <div className="bw-header">
        <span className="bw-title">BrainWaves · {timeframe ?? '—'}</span>
        <span className="bw-delta" style={{ color: deltaColor }}>
          {deltaText}
          {deltaArrow}
        </span>
      </div>

      <div className="bw-svg-container" ref={containerRef} style={{ height: h }}>
        {chartWidth > 0 &&
          (hasData && data ? (
            <svg
              width={chartWidth}
              height={h}
              style={{ display: 'block', opacity: shown ? 1 : 0, transition: 'opacity .4s ease' }}
            >
              <defs>
                <linearGradient id="eob-fill" x1="0" y1={eobY} x2="0" y2={topY} gradientUnits="userSpaceOnUse">
                  <stop offset="0" stopColor="#DD2222" stopOpacity={0.35} />
                  <stop offset="1" stopColor="#000000" stopOpacity={0.15} />
                </linearGradient>
                <linearGradient id="ob-fill" x1="0" y1={obY} x2="0" y2={eobY} gradientUnits="userSpaceOnUse">
                  <stop offset="0" stopColor="#DD2222" stopOpacity={0.06} />
                  <stop offset="1" stopColor="#DD2222" stopOpacity={0.18} />
                </linearGradient>
                <linearGradient id="eos-fill" x1="0" y1={eosY} x2="0" y2={botY} gradientUnits="userSpaceOnUse">
                  <stop offset="0" stopColor="#33AA00" stopOpacity={0.27} />
                  <stop offset="1" stopColor="#000000" stopOpacity={0.15} />
                </linearGradient>
                <linearGradient id="os-fill" x1="0" y1={osY} x2="0" y2={eosY} gradientUnits="userSpaceOnUse">
                  <stop offset="0" stopColor="#33AA00" stopOpacity={0.06} />
                  <stop offset="1" stopColor="#33AA00" stopOpacity={0.18} />
                </linearGradient>
              </defs>

              {/* OB/OS zone fills */}
              <rect x={0} y={topY} width={chartWidth} height={eobY - topY} fill="url(#eob-fill)" />
              <rect x={0} y={eobY} width={chartWidth} height={obY - eobY} fill="url(#ob-fill)" />
              <rect x={0} y={eosY} width={chartWidth} height={botY - eosY} fill="url(#eos-fill)" />
              <rect x={0} y={osY} width={chartWidth} height={eosY - osY} fill="url(#os-fill)" />

              {/* Level lines */}
              {[obY, osY, eobY, eosY].map((y, i) => (
                <line key={i} x1={0} y1={y} x2={chartWidth} y2={y} stroke="rgba(178,181,190,0.5)" strokeWidth={0.7} />
              ))}
              <line
                x1={0}
                y1={midY}
                x2={chartWidth}
                y2={midY}
                stroke="rgba(255,108,0,0.4)"
                strokeWidth={0.8}
                strokeDasharray="4,4"
              />

              {/* Histogram */}
              {data.histogram.map((hv, i) => {
                if (i === 0) return null;
                const rising = hv > data.histogram[i - 1];
                const alpha = 0.1 + (Math.abs(hv) / 100) * 0.6;
                const col = rising ? `rgba(166,255,0,${alpha})` : `rgba(255,136,0,${alpha})`;
                return (
                  <line
                    key={`h${i}`}
                    x1={toX(i, data.histogram.length)}
                    y1={midY}
                    x2={toX(i, data.histogram.length)}
                    y2={toY(hv)}
                    stroke={col}
                    strokeWidth={1}
                  />
                );
              })}

              {/* Fill between wt1/wt2 */}
              {data.wt1.map((_, i) => {
                if (i === 0) return null;
                const n = data.wt1.length;
                const x1 = toX(i - 1, n);
                const x2 = toX(i, n);
                const col = withAlpha(posColor(data.ribbon[i]), 0.2);
                const pts = [
                  `${x1},${toY(data.wt1[i - 1])}`,
                  `${x2},${toY(data.wt1[i])}`,
                  `${x2},${toY(data.wt2[i])}`,
                  `${x1},${toY(data.wt2[i - 1])}`,
                ].join(' ');
                return <polygon key={`f${i}`} points={pts} fill={col} />;
              })}

              {/* Ghost wt1 */}
              {data.wt1.map((_, i) => {
                if (i === 0) return null;
                const n = data.wt1.length;
                return (
                  <line
                    key={`g1${i}`}
                    x1={toX(i - 1, n)}
                    y1={toY(data.wt1[i - 1])}
                    x2={toX(i, n)}
                    y2={toY(data.wt1[i])}
                    stroke={withAlpha(posColor(data.ribbon[i]), 0.45)}
                    strokeWidth={1}
                  />
                );
              })}

              {/* Ghost wt2 */}
              {data.wt2.map((_, i) => {
                if (i === 0) return null;
                const n = data.wt2.length;
                return (
                  <line
                    key={`g2${i}`}
                    x1={toX(i - 1, n)}
                    y1={toY(data.wt2[i - 1])}
                    x2={toX(i, n)}
                    y2={toY(data.wt2[i])}
                    stroke={withAlpha(posColor(data.ribbon[i]), 0.45)}
                    strokeWidth={1}
                  />
                );
              })}

              {/* Ribbon */}
              {ribbonSegments}

              {/* Cross dots */}
              {data.crosses.map((c, i) => {
                if (c.x < 0 || c.x >= data.ribbon.length) return null;
                const cx = toX(c.x, data.ribbon.length);
                const cy = toY(data.ribbon[c.x]);
                const outerR = c.extreme ? 5 : 3;
                const innerR = c.extreme ? 4 : 2;
                const innerCol = c.type === 'bullish' ? '#33AA00' : '#DD2222';
                return (
                  <React.Fragment key={`c${i}`}>
                    <circle cx={cx} cy={cy} r={outerR} fill="rgba(255,255,255,0.6)" />
                    <circle cx={cx} cy={cy} r={innerR} fill={innerCol} />
                  </React.Fragment>
                );
              })}
            </svg>
          ) : (
            <div className="bw-placeholder">
              <span className="bw-placeholder-text">Awaiting data…</span>
            </div>
          ))}
      </div>
    </GradientBorder>
  );
}
